import { Component, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbNavModule } from '@ng-bootstrap/ng-bootstrap';
import { TableModule } from 'primeng/table';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { FormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ChildrenSelectorComponent, Student } from '../../shared/children-selector/children-selector.component';
import { PageTitleComponent } from '../../shared/page-title/page-title.component';
import {
  DisciplineService,
  DisciplineItem,
  PunitionItem,
  ApiResponse,
  AdminPunitionItem,
  AdminDisciplineItem
} from '../../services/discipline.service';
import { NotificationService } from '../../services/notification.service';
import { catchError, forkJoin, of, Subject, takeUntil } from 'rxjs';
import { LoaderComponent } from '../../shared/loader/loader.component';

@Component({
  selector: 'app-discipline',
  standalone: true,
  imports: [
    CommonModule,
    NgbNavModule,
    TableModule,
    InputTextModule,
    ButtonModule,
    FormsModule,
    ChildrenSelectorComponent,
    PageTitleComponent,
    TranslatePipe,
    LoaderComponent
  ],
  templateUrl: './discipline.component.html',
  styleUrls: ['./discipline.component.css']
})
export class DisciplineComponent implements OnDestroy {
  private destroy$ = new Subject<void>();

  selectedChild: string = '';
  selectedStudentId: number | null = null;
  selectedBatchId: number | null = null;
  selectedUserId: number | null = null;
  selectedNameOrg: string = '';
  selectedOrgId: number | null = null; // ✅ ajouté pour corriger l’erreur

  activeTab: string = 'discipline';
  loading = false;
  error: string | null = null;

  disciplineRecords: DisciplineItem[] = [];
  filteredDisciplineRecords: DisciplineItem[] = [];
  punitionRecords: PunitionItem[] = [];
  adminPunitionRecords: AdminPunitionItem[] = [];
  adminDisciplineRecords: AdminDisciplineItem[] = [];

  constructor(
    private service: DisciplineService,
    private translate: TranslateService,
    private notificationService: NotificationService
  ) {}
  
  // Format a date string to `yyyy-MM-dd HH:mm` (no seconds)
  formatDateFlag(dateStr?: string | null): string {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
      d.getDate()
    )} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  // Normalize horaire to `HH:mm:ss`. Accepts formats like `10h`, `10:04`, `10:04:15`.
  formatHoraire(horaire?: string | null): string {
    if (!horaire) return '-';
    // If it's already in the form "HH:mm:ss"
    const h = horaire.trim();
    // If contains '==' used elsewhere, convert to 'à' and take first part
    const cleaned = h.split('==>').join(' à ');
    const firstPart = cleaned.split(' à ')[0].trim();

    // If format like 10h or 10h30
    const hMatch = firstPart.match(/^(\d{1,2})h(\d{1,2})?$/);
    if (hMatch) {
      const hh = hMatch[1].padStart(2, '0');
      const mm = (hMatch[2] || '00').padStart(2, '0');
      return `${hh}:${mm}:00`;
    }

    // If format like HH:mm or HH:mm:ss
    const colonParts = firstPart.split(':');
    if (colonParts.length === 2) {
      const hh = colonParts[0].padStart(2, '0');
      const mm = colonParts[1].padStart(2, '0');
      return `${hh}:${mm}:00`;
    }
    if (colonParts.length >= 3) {
      const hh = colonParts[0].padStart(2, '0');
      const mm = colonParts[1].padStart(2, '0');
      const ss = colonParts[2].padStart(2, '0');
      return `${hh}:${mm}:${ss}`;
    }

    // If it's a plain number like '10' -> 10:00:00
    const numMatch = firstPart.match(/^\d{1,2}$/);
    if (numMatch) {
      return `${firstPart.padStart(2, '0')}:00:00`;
    }

    // Fallback: return original
    return firstPart;
  }

  // Short date format: D-M-YYYY (no leading zeros)
  formatDateShort(dateStr?: string | null): string {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return `${d.getDate()}-${d.getMonth() + 1}-${d.getFullYear()}`;
  }

  // Short horaire: return only hour with 'h', e.g. '10h'
  formatHoraireHour(horaire?: string | null): string {
    if (!horaire) return '-';
    const h = horaire.trim();
    const cleaned = h.split('==>').join(' à ');
    const firstPart = cleaned.split(' à ')[0].trim();

    const hMatch = firstPart.match(/^(\d{1,2})h(\d{1,2})?$/);
    if (hMatch) {
      return `${parseInt(hMatch[1], 10)}h`;
    }

    const colonParts = firstPart.split(':');
    if (colonParts.length >= 1 && /^\d+$/.test(colonParts[0])) {
      return `${parseInt(colonParts[0], 10)}h`;
    }

    const numMatch = firstPart.match(/^\d{1,2}$/);
    if (numMatch) {
      return `${parseInt(firstPart, 10)}h`;
    }

    // If nothing meaningful, return '-' to avoid showing an empty horaire
    return firstPart || '-';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onStudentSelected(student: Student): void {
    this.selectedChild = student.nameStudent;
    this.selectedStudentId = student.idStudent;
    this.selectedBatchId = student.batchId;
    this.selectedUserId = student.idUserStudent;
    this.selectedNameOrg = student.nameOrg;
    this.selectedOrgId = student.idOrg || 0; // ✅ récupération de l’organisation depuis le Student

    this.loadAllTables();
  }

  setActiveTab(tab: string): void {
    this.activeTab = tab;
  }

  private loadAllTables(): void {
    if (
      !this.selectedStudentId ||
      !this.selectedBatchId ||
      !this.selectedUserId ||
      !this.selectedNameOrg ||
      this.selectedOrgId === null
    )
      return;

    this.loading = true;
    this.error = null;

    forkJoin({
      discipline: this.service
        .getDisciplinePerPage(
          this.selectedStudentId,
          this.selectedBatchId,
          this.selectedUserId,
          1,
          1000
        )
        .pipe(
          catchError((err) => {
            console.error('Discipline API error', err);
            this.error = 'Failed to load discipline';
            return of({ response: [] } as ApiResponse<DisciplineItem>);
          })
        ),

      punition: this.service
        .getPunitionPerPage(
          this.selectedNameOrg, // nameOrg
          this.selectedOrgId, // idOrganization ✅ corrigé
          this.selectedStudentId, // idStudent
          this.selectedBatchId, // idBatch
          this.selectedUserId, // idUser
          1, // numberPage
          1000 // sizePage
        )
        .pipe(
          catchError((err) => {
            console.error('Punition API error', err);
            this.error = 'Failed to load punition';
            return of({ response: [] } as ApiResponse<PunitionItem>);
          })
        )
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ discipline, punition }) => {
        const disciplineList = Array.isArray(discipline.response)
          ? discipline.response
          : [];
        this.disciplineRecords = disciplineList;
        this.filteredDisciplineRecords = disciplineList.filter(
          (item) => item.type !== 'present'
        );

        const punitionList = Array.isArray(punition.response)
          ? punition.response
          : [];
        this.punitionRecords = punitionList;

        this.markDisciplineNotificationsAsSeen(disciplineList);
        this.markPunitionNotificationsAsSeen(punitionList);

        this.loading = false;
      });
  }

  private markDisciplineNotificationsAsSeen(
    disciplineRecords: DisciplineItem[]
  ): void {
    if (!this.selectedStudentId) return;

    disciplineRecords.forEach((discipline) => {
      if (discipline.id && this.selectedStudentId) {
        this.notificationService
          .updateNotificationStatus({
            idType: discipline.id,
            idStudent: this.selectedStudentId,
            nameType: 'Discipline'
          })
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              console.log(
                `Discipline notification ${discipline.id} marked as seen`
              );
              this.updateNotificationCounts();
            },
            error: (err) =>
              console.error('Error updating discipline notification:', err)
          });
      }
    });
  }

  private markPunitionNotificationsAsSeen(punitionRecords: PunitionItem[]): void {
    if (!this.selectedStudentId) return;

    punitionRecords.forEach((punition) => {
      if (punition.id && this.selectedStudentId) {
        this.notificationService
          .updateNotificationStatus({
            idType: punition.id,
            idStudent: this.selectedStudentId,
            nameType: 'Punition'
          })
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              console.log(
                `Punition notification ${punition.id} marked as seen`
              );
              this.updateNotificationCounts();
            },
            error: (err) =>
              console.error('Error updating punition notification:', err)
          });
      }
    });
  }

  private updateNotificationCounts(): void {
    const userDataStr = localStorage.getItem('userData');
    if (userDataStr) {
      try {
        const userData = JSON.parse(userDataStr);
        if (userData.iduser) {
          this.notificationService
            .getNotificationCounts(userData.iduser.toString())
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              error: (err) =>
                console.error('Error updating notification counts:', err)
            });
        }
      } catch (e) {
        console.error('Error parsing userData from localStorage', e);
      }
    }
  }
}
